import { loadStripe, Stripe } from '@stripe/stripe-js';
import { useEffect, useState, useRef } from 'react';
import { useQuery } from 'react-query';
import axios from '../axios';
import mixpanel from '../mixpanelLogger';
import { notify } from '../Notification';

type UseInitializerProps = {
  portal: string;
  useStripe?: boolean;
  onAfterInit?: () => void;
};

type HelloResponse = {
  version_number: string;
  instance: string;
  debug: boolean;
  server_datetime: string;
  stripe_public_api_key?: string;
  mixpanel_api_token?: string;
  success?: boolean;
  google_maps_api_key?: string;
  google_maps_map_id?: string;
  source_commit?: string;
  source_branch?: string;
  environment?: string;
  token_refresh_interval_ms: number;
  show_live_dentist_verification_toggle?: boolean;
};

const initMixpanel = (apiKey: string, debug: boolean) => {
  mixpanel.init(apiKey, { debug, persistence: 'localStorage' });
};

function useInitializer({
  portal,
  useStripe = false,
  onAfterInit = () => {},
}: UseInitializerProps) {
  const [environment, setEnvironment] = useState<string | null>(null);
  const [version, setVersion] = useState<string>('');
  const [debug, setDebug] = useState<boolean>(false);
  const [serverDateTime, setServerDateTime] = useState<string | null>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [fullResponse, setFullResponse] = useState<HelloResponse | null>(null);
  const initialized = useRef(false);

  const helloUrl = `hello/?mixpanel_project=${portal}`;

  const fetchHello = async () => {
    const response = await axios.get<HelloResponse>(helloUrl);
    return response.data;
  };

  const processData = async (data: HelloResponse) => {
    setVersion(data.version_number);
    setEnvironment(data.instance);
    setDebug(data.debug);
    setServerDateTime(data.server_datetime);

    if (useStripe && data.stripe_public_api_key) {
      setStripe(await loadStripe(data.stripe_public_api_key));
    }

    if (data.mixpanel_api_token) {
      initMixpanel(data.mixpanel_api_token, data.debug);
    }

    setFullResponse(data);
    onAfterInit();
  };

  const { refetch, isLoading } = useQuery<HelloResponse, Error>({
    queryKey: ['hello', portal],
    queryFn: fetchHello,
    enabled: false,
    retry: 1,
    staleTime: Infinity, // data doesn't go stale
    onSuccess: (data) => {
      processData(data);
    },
    onError: (error) => {
      notify(error.message, 'error');
    },
  });

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    refetch();
  }, []);

  // This code will override the date sent to Mixpanel
  // DEV ONLY - Comment this block out when testing new Mixpanel events
  // TODO: How do we get the actual date to Mixpanel?
  useEffect(() => {
    const setMockDate = async () => {
      if (debug && serverDateTime) {
        const MockDate = await import('mockdate');
        MockDate.set(serverDateTime);
      }
    };
    setMockDate();
  }, [debug, serverDateTime]);

  return {
    environment,
    version,
    debug,
    serverDateTime,
    stripe,
    fullResponse,
    reinitialize: () => refetch(),
    isLoading,
  };
}

export default useInitializer;
